import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TePreviewLinksPanel } from '@/components/TePreviewLinksPanel';
import {
  Users, Send, FileText, Package, Loader2, Plus, ExternalLink,
  CheckCircle2, XCircle, BookOpen, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /rep-dashboard — sales rep's own portal.
 * Shows my leads / invites / quotes / orders + a "Send Demo Invite" action.
 */
export default function RepDashboard() {
  const [me, setMe] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newInv, setNewInv] = useState({ teacher_name: '', teacher_email: '', school_name: '', grade_band: '3-4' });
  const [submitting, setSubmitting] = useState(false);

  // v6.38.5 — accept ?impersonate=<token> so super admin can jump
  // straight into a rep's portal from the Sales Reps management tab.
  // The token replaces localStorage.session_token for THIS tab only.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('impersonate');
    if (tok) {
      localStorage.setItem('session_token', tok);
      localStorage.setItem('impersonating', 'true');
      // Clean the URL so a refresh doesn't reapply
      window.history.replaceState({}, '', '/rep-dashboard');
    }
  }, []);

  const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [meR, invR] = await Promise.all([
        axios.get(`${API}/api/rep/me`, H()),
        axios.get(`${API}/api/rep/demo-invites`, H()),
      ]);
      setMe(meR.data);
      setInvites(invR.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rep portal');
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const submitInvite = async () => {
    if (!newInv.teacher_name || !newInv.teacher_email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/demo-invites`, newInv, H());
      toast.success('Invite created — copy the link and send it.');
      navigator.clipboard?.writeText(r.data.invite.redeem_url);
      toast.info('Link copied to clipboard');
      setNewInv({ teacher_name: '', teacher_email: '', school_name: '', grade_band: '3-4' });
      setShowNew(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invite');
    }
    setSubmitting(false);
  };

  const toggleInvite = async (invite_id, currentStatus) => {
    const next = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await axios.patch(`${API}/api/demo-invites/${invite_id}`, { status: next }, H());
      toast.success(`Invite ${next}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const copy = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success('Copied to clipboard');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-blue-600" size={40} />
    </div>
  );

  const c = me?.counts || {};
  const public_host = window.location.origin;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="rep-dashboard">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Welcome, {me?.user?.name}</h1>
            <p className="text-sm text-slate-600 mt-1">
              MOSAIC Rep Portal · {me?.user?.territory || 'No territory assigned'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="rep-new-invite-btn">
              <Send size={16} className="mr-2" /> Send Demo Invite
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/rep-playbook'} data-testid="rep-playbook-btn">
              <BookOpen size={16} className="mr-2" /> Playbook
            </Button>
          </div>
        </div>

        {/* KPI grid */}
        <TePreviewLinksPanel />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { icon: Users, label: 'Leads', value: c.leads, color: 'text-purple-700' },
            { icon: Send, label: 'Active invites', value: c.invites_active, color: 'text-blue-700' },
            { icon: CheckCircle2, label: 'Redeemed', value: c.invites_redeemed, color: 'text-emerald-700' },
            { icon: FileText, label: 'Quotes', value: c.quotes, color: 'text-amber-700' },
            { icon: Package, label: 'Orders', value: c.orders, color: 'text-rose-700' },
          ].map((k, i) => (
            <Card key={i} className="p-4">
              <div className={`flex items-center gap-2 text-xs uppercase tracking-wider ${k.color}`}>
                <k.icon size={14} /> {k.label}
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2">{k.value ?? 0}</div>
            </Card>
          ))}
        </div>

        {/* New Invite Modal-ish */}
        {showNew && (
          <Card className="p-5 border-2 border-blue-400 bg-blue-50">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-bold text-blue-900">Send a Demo Invite</h3>
              <button onClick={() => setShowNew(false)} className="text-slate-500">✕</button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase text-slate-500">Prospect name</label>
                <Input value={newInv.teacher_name} onChange={e => setNewInv({...newInv, teacher_name: e.target.value})} className="mt-1" data-testid="rep-invite-name" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500">Email</label>
                <Input type="email" value={newInv.teacher_email} onChange={e => setNewInv({...newInv, teacher_email: e.target.value})} className="mt-1" data-testid="rep-invite-email" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500">School name</label>
                <Input value={newInv.school_name} onChange={e => setNewInv({...newInv, school_name: e.target.value})} className="mt-1" data-testid="rep-invite-school" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500">Grade band</label>
                <select value={newInv.grade_band} onChange={e => setNewInv({...newInv, grade_band: e.target.value})} className="mt-1 w-full border rounded px-2 py-2" data-testid="rep-invite-band">
                  <option value="K-2">K-2</option>
                  <option value="3-4">3-4</option>
                  <option value="5-6">5-6</option>
                  <option value="7-8">7-8</option>
                  <option value="9-12">9-12</option>
                </select>
              </div>
            </div>
            <Button onClick={submitInvite} disabled={submitting} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="rep-invite-submit">
              {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
              Create & copy link
            </Button>
          </Card>
        )}

        {/* Invites table */}
        <Card className="p-5">
          <h3 className="font-bold text-slate-900 mb-3">My Demo Invites</h3>
          {invites.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No invites yet. Click <b>Send Demo Invite</b> above to invite your first prospect.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b">
                    <th className="text-left py-2">Prospect</th>
                    <th className="text-left py-2">School</th>
                    <th className="text-left py-2">Grade</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Logins</th>
                    <th className="text-left py-2">Last seen</th>
                    <th className="text-left py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(inv => {
                    const url = `${public_host}/demo-invite/${inv.token}`;
                    return (
                      <tr key={inv.id} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="py-2">
                          <div className="font-medium">{inv.teacher_name}</div>
                          <div className="text-xs text-slate-500">{inv.teacher_email}</div>
                        </td>
                        <td className="py-2">{inv.school_name || '—'}</td>
                        <td className="py-2">{inv.grade_band}</td>
                        <td className="py-2">
                          <Badge className={inv.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}>
                            {inv.status}
                          </Badge>
                          {inv.redeemed_at && <Badge className="ml-1 bg-blue-100 text-blue-800">redeemed</Badge>}
                        </td>
                        <td className="py-2 text-slate-700">{inv.login_count || 0}</td>
                        <td className="py-2 text-xs text-slate-500">{inv.last_seen_at ? new Date(inv.last_seen_at).toLocaleDateString() : '—'}</td>
                        <td className="py-2 flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => copy(url)} title="Copy invite link"><Copy size={12} /></Button>
                          <Button size="sm" variant="outline" onClick={() => toggleInvite(inv.id, inv.status)} title={inv.status === 'active' ? 'Disable' : 'Re-enable'}>
                            {inv.status === 'active' ? <XCircle size={12} /> : <CheckCircle2 size={12} />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}
